import type { HuecoCliente, ResultadoHuecosCliente } from '../api/ops/citasCliente';
import { Fecha } from '../componentes/Fecha';
import { Icono } from '../componentes/Icono';
import { ciudadDeZona, diaCorto, gruposDeHuecos, inicialDe, mayuscula } from './citas';

/**
 * «¿Con quién y cuándo?» (9.9, paso 2): con quién (si el negocio deja elegir y lo hace más de uno), el día y los huecos
 * libres por la mañana y por la tarde (`searchAppointmentSlots`); sin hueco, el motivo y «Lo más cerca» con las
 * alternativas que da la API. La hora es siempre la del negocio («Horas de Madrid»).
 */
export interface OpcionProfesional {
    id: string | null;
    nombre: string;
}

interface Props {
    profesionales: readonly OpcionProfesional[];
    profesional: string | null;
    onProfesional: (id: string | null) => void;
    dia: string;
    minDia: string;
    onDia: (dia: string) => void;
    /** `null` mientras se buscan. */
    resultado: ResultadoHuecosCliente | null;
    error: boolean;
    onReintentar: () => void;
    elegido: string | null;
    onElegir: (h: HuecoCliente) => void;
    sinHueco: { titulo: string; texto: string };
    zona: string;
    /** `oscuro` en el widget oscuro (G618): RV Fecha en oscuro. */
    tema?: 'claro' | 'oscuro';
}

export function ElegirHora({ profesionales, profesional, onProfesional, dia, minDia, onDia, resultado, error, onReintentar, elegido, onElegir, sinHueco, zona, tema = 'claro' }: Props) {
    const huecos = resultado?.slots ?? [];
    const alternativas = resultado?.alternatives ?? [];
    return (
        <>
            {profesionales.length > 2 && (
                <div className="rv-ct-pros">
                    <span>Con quién</span>
                    <div role="radiogroup" aria-label="Con quién" className="rv-ct-pastillas">
                        {profesionales.map((p) => (
                            <button key={p.id ?? 'cualquiera'} type="button" role="radio" aria-checked={profesional === p.id} className="rv-ct-pastilla" onClick={() => { if (profesional !== p.id) onProfesional(p.id); }}>
                                <span aria-hidden="true" className="rv-ct-avatar">{p.id === null ? <Icono nombre="shuffle" /> : inicialDe(p.nombre)}</span>
                                <span>{p.nombre}</span>
                            </button>
                        ))}
                    </div>
                    {profesional === null && <span className="rv-pc-nota">Con «Cualquiera» ves más horas: te atiende quien esté libre.</span>}
                </div>
            )}
            <Fecha etiqueta="Día" valor={dia} min={minDia} onChange={(v) => { if (v !== '') onDia(v); }} tema={tema} />
            {error ? (
                <div className="rv-ct-vacio" role="alert">
                    <span><Icono nombre="circle-alert" /><span><b>No hemos podido buscar las horas libres</b><span>Puede ser la conexión. Prueba de nuevo.</span></span></span>
                    <div className="rv-ct-alternativas">
                        <button type="button" className="rv-pc-b rv-pc-b--sec rv-pc-b--peq" onClick={onReintentar}><Icono nombre="refresh-cw" /><span>Reintentar</span></button>
                    </div>
                </div>
            ) : resultado === null ? (
                <div className="rv-ct-huecos" aria-busy="true" aria-label="Buscando horas libres">
                    <div className="rv-pc-esqueleto" style={{ height: 44 }} />
                    <div className="rv-pc-esqueleto" style={{ height: 44 }} />
                </div>
            ) : huecos.length > 0 ? (
                <div className="rv-ct-huecos" role="group" aria-label="Horas libres">
                    {gruposDeHuecos(huecos).map((g) => (
                        <div key={g.grupo}>
                            <span>{g.grupo}</span>
                            <div className="rv-ct-rejilla">
                                {g.huecos.map((h) => (
                                    <button
                                        key={`${h.starts_at}-${h.professional_id ?? ''}`}
                                        type="button"
                                        className="rv-ct-hueco"
                                        aria-pressed={elegido === h.starts_at}
                                        aria-label={`${h.local_time}${profesional === null && h.professional_display_name !== undefined ? `, con ${h.professional_display_name}` : ''}`}
                                        onClick={() => { onElegir(h); }}
                                    >
                                        {h.local_time}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="rv-ct-vacio">
                    <span><Icono nombre="calendar-x" /><span><b>{sinHueco.titulo}</b><span>{sinHueco.texto}</span></span></span>
                    {alternativas.length > 0 && (
                        <>
                            <strong style={{ fontSize: 13 }}>Lo más cerca</strong>
                            <div className="rv-ct-alternativas">
                                {alternativas.map((a) => (
                                    <button key={`${a.starts_at}-${a.professional_id ?? ''}`} type="button" className="rv-pc-b rv-pc-b--sec rv-pc-b--peq" onClick={() => { onElegir(a); }}>
                                        <span>{`${mayuscula(diaCorto(a.local_date))} · ${a.local_time}${profesional === null && a.professional_display_name !== undefined ? ` · con ${a.professional_display_name}` : ''}`}</span>
                                        <Icono nombre="arrow-right" />
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
            <span className="rv-ct-zona"><Icono nombre="globe" /><span>{`Horas de ${ciudadDeZona(zona)}`}</span></span>
        </>
    );
}
